<template>
  <div ref="menu" class="menu" :class="{ open: opened }" @click.self="$emit('close')">
    <div
      class="brandName hideOnSmallSize"
      :class="roleHeaderClass"
      style="--hide-on-small-display: flex"
    >
      <div class="brand-logo" :style="{ backgroundImage: `url(${logo})` }"></div>
    </div>
    <div ref="sub" class="sub__body">
      <div class="close py-3 px-4 hideOnBigSize" @click="$emit('close')">
        <i class="fa fa-times"></i>
      </div>

      <div class="user" :style="mobileHeaderStyle">
        <div
          class="avatar"
          :style="{ backgroundImage: `url(${avatarUrl})` }"
          @click="onAvatarSecretTap"
        ></div>
        <div class="info__person">
          <div class="user__name">
            <div class="d-flex">
              {{ fullName }}
            </div>
            <div class="d-flex mt-2 group__name" v-if="groupLabel">
              {{ groupLabel }}
            </div>
            <div class="d-flex mt-2 group__name" v-else>
              {{ roleLabel }}
            </div>
          </div>
        </div>
      </div>

      <div class="item" :class="menuColorClass">
        <template v-for="(item, index) in visibleItems" :key="`prev-${index}`">
          <router-link class="item__body" :to="item.to" @click="onNavigate">
            <div class="prev__icon" :class="item.classAdd">
              <span class="material-icons prev__icon__body">{{ item.icon }}</span>
            </div>
            <div class="w-100 d-flex align-items-center">
              {{ sidebarMenuLabel(item.num) }}
              <div class="newPanel" v-if="item.new">new</div>
            </div>
          </router-link>
        </template>
        <div class="border-line-bottom"></div>
      </div>

      <div class="item child" v-if="isParent && children.length">
        <div class="my__childs">
          <span>{{ t("myChilds") }}</span>
        </div>
        <div
          class="d-flex align-items-center child__body"
          v-for="child in children"
          :key="child.uin"
          :class="{ selectedChild: child.uin === selectedChildUin }"
          @click="onSelectChild(child.uin)"
        >
          <div
            class="avatar child-avatar me-3"
            :style="{ backgroundImage: `url(${childAvatar(child)})` }"
          ></div>
          <div class="childName w-100">
            <div class="w-100 text-truncate name">{{ childMenuLabel(child) }}</div>
          </div>
        </div>
        <div class="border-line-bottom"></div>
      </div>

      <div class="item">
        <div class="item__body d-flex" @click="onNavigateTo('/help')">
          <div class="prev__icon">
            <span class="material-icons">help</span>
          </div>
          <div class="cont va">
            <div class="vam w-100 text-truncate">{{ t("sidebar_help_label") }}</div>
          </div>
        </div>
      </div>
      <div class="item">
        <div class="item__body d-flex" @click="onNavigateTo('/settings')">
          <div class="prev__icon">
            <span class="material-icons">settings</span>
          </div>
          <div class="cont va">
            <div class="vam w-100 text-truncate">{{ t("sidebar_settings_label") }}</div>
          </div>
        </div>
      </div>
      <div class="version">
        {{ t("common.version_label") }} {{ appVersion }}
        <span v-if="appVersionCode">({{ appVersionCode }})</span>
      </div>
    </div>

    <Transition name="developer-toast">
      <div v-if="developerToast" class="developer-toast" role="status" aria-live="polite">
        {{ developerToast }}
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";

import bgChild from "@/assets/menu/bg-green.png";
import bgParent from "@/assets/menu/bg-blue.png";
import logo from "@/assets/menu/logo.svg";
import { useDeviceStore } from "@/stores/device";
import { useDebugStore } from "@/stores/debug";
import { useLangStore } from "@/stores/lang";
import { useViewerStore } from "@/stores/viewer";

const props = defineProps({
  opened: {
    type: Boolean,
    default: false,
  },
  windowWidth: {
    type: Number,
    default: 1280,
  },
});

const emit = defineEmits(["close"]);

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const langStore = useLangStore();
const deviceStore = useDeviceStore();
const debugStore = useDebugStore();
const viewerStore = useViewerStore();
viewerStore.initFromStorage();
const profile = computed(() => viewerStore.profile || {});
const role = computed(() => viewerStore.role || "parent");
const children = computed(() => viewerStore.children || []);
const selectedChildUin = computed(() => viewerStore.selectedChildUin);
const isParent = computed(() => viewerStore.isParent);
const appVersion = computed(() => String(deviceStore.version || "3.1.0"));
const appVersionCode = computed(() => String(deviceStore.versionCode || ""));
const secretTapCount = ref(0);
const developerToast = ref("");
let secretTapTimer = null;
let developerToastTimer = null;

const fullName = computed(() => {
  const first = profile.value?.first_name || "";
  const middle = profile.value?.middle_name || "";
  return `${first} ${middle}`.trim() || t("app.title");
});

const roleLabel = computed(() => {
  if (role.value === "child") return t("role_child");
  return t("role_parent");
});
const groupLabel = computed(() => {
  if (role.value !== "child") return "";
  const groupName = profile.value?.group?.name || profile.value?.group_name || "";
  if (!groupName) return "";

  const sex = Number(profile.value?.sex);
  const pupilLabel = sex === 2 ? t("girl-pupil") : t("boy-pupil");
  return `${pupilLabel}${groupName} ${t("inClass")}`;
});

const avatarUrl = computed(() => {
  const photo = profile.value?.photo?.small || "";
  if (photo) return photo;
  return getDefaultAvatar(role.value, profile.value?.sex);
});

const roleHeaderClass = computed(() =>
  role.value === "child" ? "bg-pupil" : "bg-parent"
);
const menuColorClass = computed(() =>
  role.value === "child" ? "color-pupil" : "color-parent"
);
const prevItems = computed(() => {
  const getter = route.meta?.subtopbar;
  if (typeof getter === "function") {
    const resolved = getter();
    return Array.isArray(resolved) ? resolved : [];
  }
  return Array.isArray(getter) ? getter : [];
});
const visibleItems = computed(() =>
  prevItems.value.filter((item) => {
    if (item.only && !(item.only === "child" && role.value === "child")) {
      return false;
    }

    // legacy parity: menu tab is available only if child has meals_privilege_type
    if (item.num === 6 || item.to === "/menu" || item.name === "menu") {
      return canShowMenuItem.value;
    }

    return true;
  })
);

const canShowMenuItem = computed(() => {
  if (role.value === "child") {
    const profileMenuType =
      profile.value?.meals_privilege_type ??
      profile.value?._data?.meals_privilege_type ??
      null;
    return profileMenuType !== null && profileMenuType !== undefined;
  }

  const selected = children.value.find((child) => child.uin === selectedChildUin.value);
  const selectedMenuType = selected?.meals_privilege_type ?? null;
  return selectedMenuType !== null && selectedMenuType !== undefined;
});

const mobileHeaderStyle = computed(() => {
  if (props.windowWidth > 767) return {};
  return {
    backgroundImage: `url(${role.value === "child" ? bgChild : bgParent})`,
  };
});

function onNavigate() {
  if (props.windowWidth <= 767) emit("close");
}

function onAvatarSecretTap() {
  secretTapCount.value += 1;

  if (secretTapTimer) clearTimeout(secretTapTimer);

  if (secretTapCount.value >= 10) {
    debugStore.toggleApiErrorsSettingVisibility();
    showDeveloperToast(
      debugStore.apiErrorsSettingVisible
        ? t("developerModeEnabled")
        : t("developerModeDisabled")
    );
    secretTapCount.value = 0;
    secretTapTimer = null;
    return;
  }

  secretTapTimer = setTimeout(() => {
    secretTapCount.value = 0;
    secretTapTimer = null;
  }, 5000);
}

function showDeveloperToast(message) {
  developerToast.value = String(message || "");
  if (developerToastTimer) clearTimeout(developerToastTimer);
  developerToastTimer = setTimeout(() => {
    developerToast.value = "";
    developerToastTimer = null;
  }, 2500);
}

function onNavigateTo(path) {
  router.push(path);
  onNavigate();
}

function sidebarMenuLabel(index) {
  return langStore.translations?.sidebarMenu?.[index] || "";
}

function childAvatar(child) {
  const avatar = child?.avatar?.small || child?.avatar || child?.photo?.small || "";
  if (typeof avatar === "string" && avatar.trim()) return avatar;
  return getDefaultAvatar("child", child?.sex);
}

function parseChildMenuGroup(child) {
  return String(child?.group_name || child?.group?.name || "").trim();
}

function childFullName(child) {
  return String(child?.first_name || "").trim() || String(child?.uin || "");
}

function childMenuLabel(child) {
  const firstName = childFullName(child);
  const group = parseChildMenuGroup(child);
  return group ? `${firstName} (${group})` : firstName;
}

function onSelectChild(uin) {
  viewerStore.selectChild(uin);

  const childRouteNames = new Set([
    "child-schedule",
    "calendar",
    "child-grades-rating",
    "child-report-card",
    "all-tasks",
    "menu",
  ]);
  if (!childRouteNames.has(String(route.name || ""))) {
    router.push("/schedule");
  }
  onNavigate();
}

function getDefaultAvatar(currentRole, sex) {
  const genderSuffix = Number(sex) === 2 ? "f" : "m";
  const prefix = currentRole === "child" ? "s" : "p";
  return `/static/img/${prefix}_${genderSuffix}.png`;
}

onMounted(() => {
  viewerStore.hydrateMissingGroupInfo();
});

onBeforeUnmount(() => {
  if (secretTapTimer) clearTimeout(secretTapTimer);
  if (developerToastTimer) clearTimeout(developerToastTimer);
});
</script>

<style scoped>
.menu {
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  z-index: -1;
  transform: translateZ(0);
  background-color: rgba(0, 0, 0, 0);
  transition: background-color 0.4s, z-index 0s linear 0.4s;
  color: #fff;
  overflow: hidden;
}

.developer-toast {
  position: fixed;
  left: 50%;
  bottom: 80px;
  z-index: 2000;
  max-width: calc(100vw - 32px);
  padding: 10px 18px;
  border-radius: 6px;
  background: rgba(32, 32, 32, 0.94);
  color: #fff;
  font-size: 14px;
  line-height: 20px;
  text-align: center;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.28);
  transform: translateX(-50%);
  pointer-events: none;
}

.developer-toast-enter-active,
.developer-toast-leave-active {
  transition: opacity 0.2s, transform 0.2s;
}

.developer-toast-enter-from,
.developer-toast-leave-to {
  opacity: 0;
  transform: translate(-50%, 8px);
}

@media screen and (min-width: 768px) {
  .developer-toast {
    bottom: 24px;
  }
}

@media screen and (min-width: 768px) {
  .menu {
    width: 260px;
    margin: 0 5% 0 15%;
    z-index: 99;
    background: transparent;
  }
}

.sub__body {
  height: 100vh;
  height: 100dvh;
  max-width: 400px;
  background: #fff;
  border-right: 1px solid #e0e0e0;
  transition: transform 0.3s;
  transform: translateX(-100%) translateZ(0);
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-gutter: stable;
}

@media screen and (max-width: 768px) {
  .sub__body {
    width: 80%;
    border-right: none !important;
  }
}

@media screen and (min-width: 768px) {
  .sub__body {
    width: 260px;
    transform: translateX(0) translateZ(0);
  }
}

.menu.open {
  z-index: 1001;
  background-color: rgba(0, 0, 0, 0.6);
  transition: background-color 0.4s, z-index 0s;
}

.menu.open .sub__body {
  transform: translateX(0) translateZ(0);
}

.brandName {
  z-index: 10;
  position: fixed;
  display: flex;
  align-items: center;
  height: 56px;
  padding-left: 18px;
  width: 260px;
}

.brand-logo {
  width: 130px;
  height: 40px;
  background-repeat: no-repeat;
  background-position: left center;
}

.bg-parent {
  background: #1c3043;
}

.bg-pupil {
  background: #164922;
}

.user {
  overflow: hidden;
  display: flex;
  border-bottom: 1px solid #e0e0e0;
}

@media screen and (max-width: 767px) {
  .user {
    flex-direction: column;
    height: 180px;
    justify-content: center;
    background-size: cover;
  }
}

@media screen and (min-width: 768px) {
  .user {
    height: 72px;
    align-items: center;
    color: #828282;
    margin-top: 56px;
  }
}

.avatar {
  border-radius: 50%;
  border: 2px solid #fff;
  margin-left: 10px;
  background-size: cover;
  background-position: center;
}

@media screen and (max-width: 768px) {
  .avatar {
    width: 64px;
    height: 64px;
  }
}

@media screen and (min-width: 768px) {
  .avatar {
    width: 52px;
    height: 52px;
  }
}

.info__person {
  margin-left: 16px;
  overflow: hidden;
  font-size: 16px;
  line-height: 1.2em;
  font-weight: bold;
}

@media screen and (max-width: 768px) {
  .info__person {
    margin-top: 12px;
    color: #fff;
  }
}

@media screen and (min-width: 768px) {
  .info__person {
    max-width: 190px;
    color: #4f4f4f;
  }
}

.group__name {
  font-size: 13px;
}

.close {
  position: absolute;
  right: 0;
  top: 0;
  opacity: 0.25;
  color: #fff;
  z-index: 2;
}

.item {
  color: #828282;
}

.item__body {
  text-decoration: none;
  margin-top: 22px;
  color: #828282;
  font-style: normal;
  height: 24px;
  display: flex;
  align-items: center;
  margin-left: 12px;
  cursor: pointer;
}

.item__body:hover {
  text-decoration: none;
}

@media screen and (max-width: 767px) {
  .item{
    margin-top: 8px;
  }
  .item__body {
    min-height: 48px;
    height: auto;
    margin-top: 0;
    margin-left: 0;
    padding: 16px 18px 12px;
  }
}

.prev__icon {
  float: left;
  font-size: 24px;
  margin-right: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.prev__icon__body {
  width: 0;
  margin-right: 24px;
}

.border-line-bottom {
  border-bottom: 1px solid #e0e0e0;
  margin-top: 10px;
}

.color-parent .router-link-exact-active {
  color: #00c2ff;
}

.color-pupil .router-link-exact-active {
  color: #36b453;
}

.version {
  display: flex;
  justify-content: center;
  font-size: 13px;
  margin-top: 10px;
  margin-bottom: 24px;
  padding-bottom: 24px;
  color: #828282;
}

.newPanel {
  background: #36b453;
  border-radius: 20px;
  font-size: 8px;
  color: #fff;
  width: 26px;
  height: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-left: 8px;
  padding-top: 1px;
  text-transform: uppercase;
}

.bellSos {
  border-radius: 24px;
  width: 24px;
  min-width: 24px;
  height: 24px;
  background: #dc3545;
  color: #fff;
}

.bellSos .prev__icon__body {
  font-size: 18px;
  margin-right: 19px;
}

.child {
  color: #828282;
  margin-top: 15px;
  padding: 0;
}

.my__childs {
  padding: 0 18px;
  margin-bottom: 15px;
}

.child__body {
  height: 64px;
  padding: 0 20px;
  cursor: pointer;
  margin: 0;
}

.selectedChild {
  background: #eef8ff;
}

.child-avatar {
  width: 40px !important;
  height: 40px !important;
  margin-left: 0;
  border: 0;
}

.childName {
  display: flex;
  align-items: center;
  min-width: 0;
}
.childName .name {
  font-size: 16px;
  line-height: 22px;
  color: #6d7580;
  font-weight: 400;
}

</style>


